import { Component, EventEmitter, forwardRef, Injector, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { CompatibleDate, DisabledDateFn, NzDateMode, SupportTimeOptions } from 'ng-zorro-antd/date-picker';
import { FunctionProp, NzSafeAny } from 'ng-zorro-antd/core/types';
import { toBoolean } from 'ng-zorro-antd/core/util';
import { noop } from 'rxjs';

@Component({
  selector: 'vnpt-rang-date-picker',
  templateUrl: './vnpt-rang-date-picker.component.html',
  styleUrls: ['../../../core/global-style/_date-picker.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => VnptRangDatePickerComponent),
    }
  ]
})
export class VnptRangDatePickerComponent implements OnInit {

  @Input() vnptLabelText: string;
  @Input() vnptAutofocus: boolean = false;
  @Input() vnptDatePickerIcon: 'search' | 'down' | string;
  @Input() vnptDisabled: boolean = false;
  @Input() vnptInputReadOnly: boolean = false;
  @Input() vnptInline: boolean = false;
  @Input() vnptMode: NzDateMode = 'date';
  @Input() vnptPlaceholderFromdate: string = null;
  @Input() vnptPlaceholderTodate: string = null;
  @Input() vnptSuffixIcon: string | TemplateRef<NzSafeAny> = 'calendar';
  @Input() vnptDisabledDate?: (d: Date) => boolean;
  @Input() vnptShowToday: boolean;
  @Input() vnptDisabledTime?: DisabledDateFn;
  @Input() vnptDateRender?: TemplateRef<NzSafeAny> | string | FunctionProp<TemplateRef<Date> | string>;
  @Input() vnptRenderExtraFooter?: TemplateRef<NzSafeAny> | string | FunctionProp<TemplateRef<NzSafeAny> | string>;
  private  showTime: SupportTimeOptions | boolean = false;

  @Output() readonly vnptOnOpenChange = new EventEmitter<boolean>();
  @Output() readonly vnptOnPanelChange = new EventEmitter<NzDateMode | NzDateMode[] | string | string[]>();
  @Output() readonly vnptOnCalendarChange = new EventEmitter<Array<Date | null>>();
  @Output() readonly vnptOnOk = new EventEmitter<CompatibleDate | null>();

  @Input() vnptErrorDefs: {errorName: string, errorDescription: string}[];
  @Input() vnptShowIconMessage = true;
  @Input() vnptShowError = false;
  @Input() vnptErrors: any;
  @Input() vnptType: 'default' | 'warning' | 'error' | 'success' = 'default';

  @Input() get mbShowTime(): SupportTimeOptions | boolean {
    return this.showTime;
  }

  set mbShowTime(value: SupportTimeOptions | boolean) {
    this.showTime = typeof value === 'object' ? value : toBoolean(value);
  }

  value: any[] = [null, null];
  onTouched: () => void = noop;
  onChange: (_: any) => void = noop;
  ngControl?: NgControl;
  inputMessageClass = 'input__message';
  textMessageValue: any;
  showIcon: any;
  iconType: any;
  inputGroupGroupClass = 'datepicker__group--group';

  classGroupGroup = {
    default: 'datepicker__group--group--default',
    warning: 'datepicker__group--group--warning',
    error: 'datepicker__group--group--error',
    success: 'datepicker__group--group--success',
  };

  classMessage = {
    default: 'datepicker__message--default',
    warning: 'datepicker__message--warning',
    error: 'datepicker__message--error',
    success: 'datepicker__message--success'
  };

  classIcon = {
    warning: 'warning',
    error: 'warning',
    success: 'check-circle'
  };

  constructor(private inj: Injector) { }

  ngOnChanges() {
    this.configInput();
    this.setErrorMessage();
  }

  ngOnInit(): void {
    this.ngControl = this.inj.get(NgControl);
  }

  writeValue(obj: any) {
    this.value = obj ?? [null, null];
    this.onChange(this.value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.vnptDisabled = isDisabled;
  }

  openChange($event, type: 'START'| 'END') {
    this.vnptOnOpenChange.emit($event)
  }

  onOk($event, type: 'START'| 'END') {
    switch (type) {
      case 'END':
        this.value[1] = $event;
        break;
      case 'START':
        this.value[0] = $event;
        break;
      default:
        return
    }
    this.vnptOnOk.emit($event)
    this.writeValue(this.value)
  }

  onInputChange($event: any,  type: 'START'| 'END') {
    switch (type) {
      case 'END':
        this.value[1] = $event;
        break;
      case 'START':
        this.value[0] = $event;
        break;
      default:
        return
    }
    this.onChange($event);
    this.writeValue(this.value)
  }

  configInput() {
    this.inputGroupGroupClass = 'datepicker__group--group' + ' ';
    this.inputMessageClass = 'datepicker__message ' + ' ';
    switch (this.vnptType) {
      case 'default':
        this.inputGroupGroupClass += this.classGroupGroup.default;
        this.inputMessageClass += this.classMessage.default;
        break;
      case 'warning':
        this.inputGroupGroupClass += this.classGroupGroup.warning;
        this.inputMessageClass += this.classMessage.warning;
        this.iconType = this.classIcon.warning;
        break;
      case 'error':
        this.inputGroupGroupClass += this.classGroupGroup.error;
        this.inputMessageClass += this.classMessage.error;
        this.iconType = this.classIcon.error;
        break;
      case 'success':
        this.inputGroupGroupClass += this.classGroupGroup.success;
        this.inputMessageClass += this.classMessage.success;
        this.iconType = this.classIcon.success;
        break;
      default:
        this.inputGroupGroupClass += this.classGroupGroup.default;
        this.inputMessageClass += this.classMessage.default;
        break;
    }
  }

  setErrorMessage() {
    if (this.vnptErrors) {
      for(let error of this.vnptErrorDefs) {
        let key = error.errorName;
        if (this.vnptErrors[key]) {
          this.textMessageValue = error.errorDescription;
        }
      }
    }
  }

}
